import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accrualsApi, type Accruals, type AccrualKind, type AccrualLine, type AccrualTotals } from '@/lib/apiClient';
import { Combobox, StateView, type ComboboxOption } from '@/components';
import { fmtDateTime } from '@/lib/dates';
import { isConflictError } from '@/lib/httpErrors';
import {
  KIND_LABEL, KIND_ORDER, UNIT_LABEL, blankDraft, currentPeriod, draftFromLine, draftProblem,
  draftTotal, draftsToPayload, fieldProblem, periodChoices, periodLabel, previousPeriod,
  subcontractDelta, sumDrafts, parseAmount, type DraftLine, type NumField,
} from '@/lib/accruals';

// Month-end accruals — the PM's schedule from the project review sheet:
// Material / Equipment / Subcontracts, one saved set per job per accounting month.
//
// A SCHEDULE ONLY. Nothing here reaches Actual, Cost to Complete or any WIP
// figure: the Intacct GL feed is the accrual book, so an accrual Accounting also
// books would be counted twice. Wiring it into the math is a separate decision.
//
// Read: financials.view. Write: financials.edit_forecast (the `canEdit` prop),
// the same gate as the buyout plan beside it.

const money = (v: number | null | undefined) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  const s = '$' + a.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(a) ? 0 : 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};
const pct = (v: number | null | undefined) => (v == null ? '—' : `${+v.toFixed(3)}%`);

const css = `
  .ac-bar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; margin-bottom:12px; }
  .ac-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .ac-select { font:var(--ew-fw-medium) 12px/1 var(--ew-font); padding:7px 10px; border:1px solid var(--ew-n-300); border-radius:var(--ew-r-md); background:#fff; color:var(--ew-dark); }
  .ac-select:disabled { opacity:.5; cursor:not-allowed; }
  .ac-btn { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius:var(--ew-r-md); padding:8px 14px; cursor:pointer; border:1px solid var(--ew-n-300); background:#fff; color:var(--ew-dark); }
  .ac-btn:hover:not(:disabled) { background:var(--ew-n-50); }
  .ac-btn.primary { background:var(--ew-green); border-color:var(--ew-green); color:#fff; }
  .ac-btn:disabled { opacity:.5; cursor:not-allowed; }
  .ac-note { display:flex; align-items:flex-start; gap:8px; background:#F6FAF7; border:1px solid #DCEBE0; border-radius:var(--ew-r-md); padding:9px 12px; margin-bottom:12px; font-size:12px; color:#3D5A46; line-height:1.45; }
  .ac-edit { background:#F4F9F5; border:1px solid #CFE6D5; border-radius:var(--ew-r-md); padding:9px 12px; margin-bottom:12px; font-size:12.5px; color:#1c5b30; line-height:1.45; }
  .ac-alert { background:#FBEEE1; border:1px solid #F0D9C0; border-radius:var(--ew-r-md); padding:9px 12px; margin-bottom:12px; font-size:12.5px; color:#8A4E12; }
  .ac-kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:var(--ew-s-3); margin-bottom:var(--ew-s-4); }
  .ac-kpi { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:12px 14px 12px 16px; position:relative; overflow:hidden; }
  .ac-kpi::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--acc,var(--ew-n-300)); }
  .ac-kpi .l { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); }
  .ac-kpi .v { font:var(--ew-fw-bold) 22px/1.1 var(--ew-font); font-variant-numeric:tabular-nums; color:var(--ew-dark); margin-top:6px; }
  .ac-kpi .s { font-size:12px; color:var(--ew-n-500); margin-top:4px; }
  .ac-sec { margin-bottom:var(--ew-s-4); }
  .ac-sec h4 { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-dark); margin:0 0 6px; padding:7px 10px; background:#E4F1F8; border-radius:var(--ew-r-sm); }
  .ac-tbl { width:100%; border-collapse:collapse; font-size:13px; }
  .ac-tbl th { text-align:right; padding:8px 10px; font:var(--ew-fw-semibold) 11px/1.2 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; color:var(--ew-n-500); border-bottom:1px solid var(--ew-n-100); white-space:nowrap; }
  .ac-tbl td { padding:7px 10px; text-align:right; font-variant-numeric:tabular-nums; border-bottom:1px solid #F2F2F1; color:var(--ew-dark); vertical-align:middle; }
  .ac-tbl th.t, .ac-tbl td.t { text-align:left; }
  .ac-tbl tfoot td { font-weight:700; border-top:2px solid var(--ew-n-200); border-bottom:none; }
  .ac-muted { color:var(--ew-n-500); }
  .ac-empty { padding:10px 12px; font-size:12.5px; color:var(--ew-n-500); border:1px dashed var(--ew-n-200); border-radius:var(--ew-r-md); }
  .ac-in { width:100%; min-width:70px; box-sizing:border-box; font:inherit; font-size:13px; padding:5px 7px; border:1px solid #CFE6D5; border-radius:3px; background:#F4F9F5; }
  .ac-in.num { text-align:right; font-variant-numeric:tabular-nums; min-width:92px; }
  .ac-in.wide { min-width:170px; }
  .ac-in.bad { border-color:#B5611A; background:#FDF6EF; }
  .ac-x { border:none; background:none; cursor:pointer; color:var(--ew-n-400); font-size:14px; padding:2px 6px; }
  .ac-x:hover { color:#B5611A; }
  .ac-add { border:none; background:none; cursor:pointer; margin-top:6px; padding:2px 0; font:var(--ew-fw-semibold) 12px/1 var(--ew-font); color:var(--ew-quality,#1F5E7A); }
  .ac-tag { display:inline-block; margin-left:6px; padding:1px 6px; border-radius:var(--ew-r-sm); font:var(--ew-fw-semibold) 9.5px/1.6 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; background:#FBEEE1; color:#B5611A; border:1px solid #F0D9C0; white-space:nowrap; }
  .ac-hist { margin-top:var(--ew-s-5); }
  .ac-hist h4 { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); margin:0 0 6px; }
  .ac-hist tr.sel td { background:#F6FAF7; font-weight:600; }
  .ac-link { border:none; background:none; padding:0; cursor:pointer; font:inherit; color:var(--ew-quality,#1F5E7A); border-bottom:1px dotted currentColor; }
  .ac-link:disabled { color:var(--ew-n-400); cursor:not-allowed; border-bottom-color:transparent; }
`;

/** A column of a section: how it reads, and how it edits. */
interface Col {
  head: string;
  /** Left-aligned text column rather than a right-aligned figure. */
  text?: boolean;
  view: (l: AccrualLine) => ReactNode;
  edit: (d: DraftLine, set: (p: Partial<DraftLine>) => void, ctx: EditCtx) => ReactNode;
}

/** The suggestion lists the vendor and cost code boxes offer. */
interface EditCtx { vendorOptions: ComboboxOption[]; codeOptions: ComboboxOption[]; }

/** Matches the panel's other boxes (`.ac-in`), which the shared Combobox would
 *  otherwise draw in its own white style. */
const FIELD_STYLE: CSSProperties = { border: '1px solid #CFE6D5', borderRadius: 3, background: '#F4F9F5', padding: '5px 7px' };

/**
 * Vendor and cost code: the app's own type-ahead, NOT a native <datalist> — whose
 * popup is browser chrome (dark, unstylable, filters its own way) and is what the
 * Combobox exists to replace. Anything typed that is not on the list is kept: an
 * accrual can name a vendor the job has no commitment with, or a cost code the
 * estimate never budgeted.
 *
 * `floating` because these sit in a table inside a horizontal scroll container,
 * which would clip an ordinary dropdown at the table's edge.
 */
const pickIn = (label: string, field: 'vendor' | 'costCode', pool: keyof EditCtx, minWidth: number): Col['edit'] =>
  (d, set, ctx) => (
    <Combobox floating ariaLabel={label} maxLength={field === 'costCode' ? 30 : 200}
      value={d[field]} onChange={(v) => set({ [field]: v })} options={ctx[pool]}
      inputStyle={{ ...FIELD_STYLE, minWidth }} />
  );

const descIn = (label: string): Col['edit'] =>
  (d, set) => (
    <input className="ac-in wide" aria-label={label} value={d.description} maxLength={300}
      onChange={(e) => set({ description: e.target.value })} />
  );

const numIn = (label: string, field: NumField): Col['edit'] =>
  (d, set) => (
    <input className={`ac-in num${fieldProblem(d, field) ? ' bad' : ''}`}
      aria-label={label} inputMode="decimal" value={d[field]} onChange={(e) => set({ [field]: e.target.value })} />
  );

const vendorCol = (head: string): Col => ({
  head, text: true, view: (l) => l.vendor ?? '—', edit: pickIn(head, 'vendor', 'vendorOptions', 150),
});
const codeCol: Col = {
  head: 'Cost code', text: true, view: (l) => l.costCode ?? '—', edit: pickIn('Cost code', 'costCode', 'codeOptions', 96),
};
const descCol = (head: string): Col => ({
  head, text: true, view: (l) => l.description ?? '—', edit: descIn(head),
});
const totalCol = (head: string, live: (d: DraftLine) => number): Col => ({
  head, view: (l) => <b>{money(l.total)}</b>, edit: (d) => <b>{money(live(d))}</b>,
});

const COLS: Record<AccrualKind, Col[]> = {
  material: [
    vendorCol('Vendor'), descCol('Material description'), codeCol,
    {
      // Read-only: last month's total for the line this one matches by vendor and
      // cost code. There is no stored link, so a respelled vendor shows nothing.
      head: 'Previous month', view: (l) => <span className="ac-muted">{money(l.prevTotal)}</span>,
      edit: () => <span className="ac-muted">·</span>,
    },
    { head: 'Carry-over', view: (l) => money(l.carryOver), edit: numIn('Carry-over', 'carryOver') },
    { head: 'Newly accrued', view: (l) => money(l.newAccrual), edit: numIn('Newly accrued', 'newAccrual') },
    totalCol('Total accrued', draftTotal),
  ],
  equipment: [
    vendorCol('Vendor'), descCol('Equipment description'), codeCol,
    {
      head: 'Unit', text: true, view: (l) => (l.unit ? UNIT_LABEL[l.unit] : '—'),
      edit: (d, set) => (
        <select className="ac-select" aria-label="Unit" value={d.unit} onChange={(e) => set({ unit: e.target.value as DraftLine['unit'] })}>
          <option value="">—</option>
          {(Object.keys(UNIT_LABEL) as (keyof typeof UNIT_LABEL)[]).map((u) => <option key={u} value={u}>{UNIT_LABEL[u]}</option>)}
        </select>
      ),
    },
    { head: 'Cost per unit', view: (l) => money(l.unitCost), edit: numIn('Cost per unit', 'unitCost') },
    { head: 'Units', view: (l) => (l.units == null ? '—' : String(l.units)), edit: numIn('Units', 'units') },
    totalCol('Total cost', draftTotal),
  ],
  subcontract: [
    vendorCol('Subcontractor'), codeCol,
    { head: 'Contract value', view: (l) => money(l.contractValue), edit: numIn('Contract value', 'contractValue') },
    { head: '% complete', view: (l) => pct(l.pctComplete), edit: numIn('Percent complete', 'pctComplete') },
    { head: '% billed to date', view: (l) => pct(l.pctBilled), edit: numIn('Percent billed to date', 'pctBilled') },
    {
      head: 'Delta', view: (l) => <DeltaCell complete={l.pctComplete} billed={l.pctBilled} />,
      edit: (d) => <DeltaCell complete={parseAmount(d.pctComplete)} billed={parseAmount(d.pctBilled)} />,
    },
    totalCol('Total accrued', draftTotal),
  ],
};

/** Percent complete minus percent billed. Negative means billed ahead of the
 *  work — shown as it falls, with a flag, never clamped to zero. */
function DeltaCell({ complete, billed }: { complete: number | null; billed: number | null }) {
  const d = subcontractDelta(complete, billed);
  return (
    <span>
      {pct(d)}
      {d < 0 && <span className="ac-tag" title="Billed ahead of the work, so the accrual is negative. Check the percentages.">Billed ahead</span>}
    </span>
  );
}

const ADD_LABEL: Record<AccrualKind, string> = { material: '+ Add material line', equipment: '+ Add equipment line', subcontract: '+ Add subcontract line' };

interface Props {
  projectId: number;
  /** Holds financials.edit_forecast (or is an admin). */
  canEdit: boolean;
  /** The job's cost codes, offered as suggestions. Free text is still accepted:
   *  an accrual can sit on a code the estimate never budgeted. */
  costCodes: { value: string; label: string }[];
  /** Vendors already on the job, offered as suggestions. */
  vendors: string[];
  /** Reports whether a draft is open, so the host can lock its view switcher
   *  while there are unsaved lines. Must be a stable function (a state setter). */
  onEditingChange?: (editing: boolean) => void;
}

/**
 * ⚠️ Keyed by project, here and not only by the host. Everything this panel holds
 * — the open draft, the chosen month, the version the draft was started from — is
 * about ONE job. Navigating straight from one job to another reuses this
 * component (same route, same position), so without the key an open draft
 * survives into the next job; and when both jobs' months are empty their versions
 * are identical, so the stale draft SAVES onto the wrong job with nothing to
 * refuse it (Codex on #590). Keying discards the instance instead of patching each
 * field, so the next piece of state added here is safe by default.
 */
export default function AccrualsPanel(props: Props) {
  return <AccrualsPanelBody key={props.projectId} {...props} />;
}

function AccrualsPanelBody({ projectId, canEdit, costCodes, vendors, onEditingChange }: Props) {
  const qc = useQueryClient();
  const [period, setPeriod] = useState(() => currentPeriod());
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<DraftLine[]>([]);
  // The version of the month the draft was seeded from — captured when the edit
  // STARTED, not read from the latest data at save time. A background refetch can
  // bring in someone else's newer save while the PM is typing; sending the newer
  // version would let this stale draft replace it, which is exactly the loss the
  // check exists to prevent.
  const [editVersion, setEditVersion] = useState('');

  // ⚠️ No keepPreviousData: it would show the OLD month's lines under the NEW
  // month's heading until the fetch landed — on a financial schedule, a wrong
  // answer is worse than a brief "Loading…".
  const q = useQuery({
    queryKey: ['accruals', projectId, period],
    queryFn: () => accrualsApi.get(projectId, period),
    staleTime: 60_000,
  });

  const saveMut = useMutation({
    mutationFn: (v: { period: string; expectedVersion: string; lines: ReturnType<typeof draftsToPayload> }) =>
      accrualsApi.save(projectId, v.period, v.expectedVersion, v.lines),
    onSuccess: (data) => {
      qc.setQueryData(['accruals', projectId, data.period], data);
      // Other months' cached payloads carry a stale history list; mark them
      // stale without refetching the ones nobody is looking at.
      qc.invalidateQueries({ queryKey: ['accruals', projectId], refetchType: 'none' });
      setEditing(false);
      setDrafts([]);
    },
  });

  useEffect(() => {
    onEditingChange?.(editing);
    return () => onEditingChange?.(false);
  }, [editing, onEditingChange]);

  const editCtx = useMemo<EditCtx>(() => ({
    vendorOptions: vendors.map((v) => ({ value: v })),
    codeOptions: costCodes.map((c) => ({ value: c.value, sub: c.label || undefined })),
  }), [vendors, costCodes]);

  const problems = useMemo(() => drafts.map(draftProblem), [drafts]);
  const firstProblem = problems.find((p) => p) ?? null;
  const liveTotals: AccrualTotals | null = editing ? sumDrafts(drafts) : null;

  // Someone else saved this month after the draft was started. The server refused
  // the save; nothing was changed. Saving again would only be refused again.
  const conflict = saveMut.isError && isConflictError(saveMut.error);

  const startEdit = (loaded: Accruals) => {
    const seeded = loaded.lines.map(draftFromLine);
    // A section with nothing in it still gets one empty row to type into.
    for (const k of KIND_ORDER) if (!seeded.some((d) => d.kind === k)) seeded.push(blankDraft(k));
    saveMut.reset();
    setDrafts(seeded);
    setEditVersion(loaded.version);
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDrafts([]); saveMut.reset(); };
  /** Drop the stale draft and fetch the month as it stands now. */
  const discardAndReload = () => {
    cancelEdit();
    qc.invalidateQueries({ queryKey: ['accruals', projectId, period] });
  };
  const setRow = (key: string, patch: Partial<DraftLine>) =>
    setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const removeRow = (key: string) => setDrafts((ds) => ds.filter((d) => d.key !== key));
  const addRow = (kind: AccrualKind) => setDrafts((ds) => [...ds, blankDraft(kind)]);
  const save = () => saveMut.mutate({ period, expectedVersion: editVersion, lines: draftsToPayload(drafts) });

  return (
    <>
      <style>{css}</style>

      <StateView query={q}>
        {(data) => {
          const totals = liveTotals ?? data.totals;
          const prev = data.previous;
          const delta = prev ? totals.total - prev.total : null;
          // The month being shown is always a choice, even with nothing saved for it.
          // Without it, clearing a month older than the picker's rolling window drops
          // it from the saved list, so the controlled select no longer holds its value:
          // the browser shows a different month while the state still targets the old one,
          // and a later save lands on a month the picker does not show (Codex on #590).
          const choices = periodChoices([...data.periods.map((p) => p.period), period]);
          const hasLines = data.lines.length > 0;

          return (
            <>
              <div className="ac-bar">
                <div className="ac-actions">
                  <label className="ac-muted" htmlFor="ac-month" style={{ fontSize: 12 }}>Accrued for</label>
                  <select id="ac-month" className="ac-select" value={period} disabled={editing}
                    onChange={(e) => setPeriod(e.target.value)}>
                    {choices.map((p) => <option key={p} value={p}>{periodLabel(p)}</option>)}
                  </select>
                </div>
                <div className="ac-actions">
                  {!editing && canEdit && (
                    <button className="ac-btn" onClick={() => startEdit(data)}>{hasLines ? '✎ Edit accruals' : '✎ Enter accruals'}</button>
                  )}
                  {editing && <>
                    <button className="ac-btn" onClick={cancelEdit} disabled={saveMut.isPending}>Cancel</button>
                    <button className="ac-btn primary" onClick={save} disabled={saveMut.isPending || firstProblem !== null || conflict}>
                      {saveMut.isPending ? 'Saving…' : 'Save accruals'}
                    </button>
                  </>}
                </div>
              </div>

              <div className="ac-note">
                <span>ℹ️</span>
                <span><b>This is your month-end schedule.</b> It does not change Actual, Cost to Complete or any other figure on the Financials tab.</span>
              </div>

              {editing && (
                <div className="ac-edit">
                  ✎ Enter what is accrued for <b>{periodLabel(period)}</b>. <b>Total accrued</b> is worked out for you: Material is carry-over plus newly accrued, Equipment is cost per unit × units,
                  and Subcontracts is contract value × (% complete − % billed to date). Empty rows are ignored.
                </div>
              )}
              {editing && firstProblem && <div className="ac-alert">⚠️ {firstProblem}</div>}
              {conflict && (
                <div className="ac-alert" role="alert">
                  ⚠️ <b>Someone else saved accruals for {periodLabel(period)} after you opened it.</b> Your changes were not saved,
                  so theirs are safe. Reload to see what they entered, then re-enter yours.{' '}
                  <button className="ac-link" onClick={discardAndReload}>Discard my changes and reload</button>
                </div>
              )}
              {saveMut.isError && !conflict && <div className="ac-alert">⚠️ Could not save: {(saveMut.error as Error)?.message ?? 'unknown error'}</div>}

              <div className="ac-kpis">
                <div className="ac-kpi" style={{ ['--acc' as any]: 'var(--ew-dark)' }}>
                  <div className="l">Total accrued</div><div className="v">{money(totals.total)}</div>
                  <div className="s">{periodLabel(period)}{editing ? ' · unsaved' : ''}</div>
                </div>
                {KIND_ORDER.map((k) => (
                  <div className="ac-kpi" key={k}>
                    <div className="l">{KIND_LABEL[k]}</div><div className="v">{money(totals[k])}</div>
                  </div>
                ))}
                <div className="ac-kpi">
                  <div className="l">Previous month</div>
                  <div className="v">{prev ? money(prev.total) : '—'}</div>
                  <div className="s">
                    {prev
                      ? `${periodLabel(prev.period)}${delta != null && Math.abs(delta) >= 0.005 ? ` · ${delta > 0 ? '+' : '−'}${money(Math.abs(delta))}` : ''}`
                      : `Nothing saved for ${periodLabel(previousPeriod(period))}`}
                  </div>
                </div>
              </div>

              {!editing && !hasLines && (
                <div className="ac-empty" style={{ marginBottom: 'var(--ew-s-4)' }}>
                  No accruals saved for {periodLabel(period)}.{canEdit ? ' Use Enter accruals to add them.' : ''}
                </div>
              )}

              {(editing || hasLines) && KIND_ORDER.map((kind) => {
                const cols = COLS[kind];
                const viewLines = data.lines.filter((l) => l.kind === kind);
                const editRows = drafts.filter((d) => d.kind === kind);
                const rowCount = editing ? editRows.length : viewLines.length;
                return (
                  <div className="ac-sec" key={kind}>
                    <h4>{KIND_LABEL[kind]}</h4>
                    {rowCount === 0 ? (
                      <div className="ac-empty">No {KIND_LABEL[kind].toLowerCase()} accruals for this month.</div>
                    ) : (
                      <div className="ew-table-scroll">
                        <table className="ac-tbl">
                          <thead>
                            <tr>
                              {cols.map((c) => <th key={c.head} className={c.text ? 't' : undefined}>{c.head}</th>)}
                              {editing && <th aria-label="Remove" />}
                            </tr>
                          </thead>
                          <tbody>
                            {editing
                              ? editRows.map((d) => {
                                  const set = (p: Partial<DraftLine>) => setRow(d.key, p);
                                  return (
                                    <tr key={d.key}>
                                      {cols.map((c) => <td key={c.head} className={c.text ? 't' : undefined}>{c.edit(d, set, editCtx)}</td>)}
                                      <td><button className="ac-x" aria-label={`Remove ${KIND_LABEL[kind].toLowerCase()} line`} onClick={() => removeRow(d.key)} disabled={saveMut.isPending}>✕</button></td>
                                    </tr>
                                  );
                                })
                              : viewLines.map((l) => (
                                  <tr key={l.id}>
                                    {cols.map((c) => <td key={c.head} className={c.text ? 't' : undefined}>{c.view(l)}</td>)}
                                  </tr>
                                ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td className="t" colSpan={cols.length - 1}>Total {KIND_LABEL[kind].toLowerCase()}</td>
                              <td>{money(totals[kind])}</td>
                              {editing && <td />}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                    {editing && <button className="ac-add" onClick={() => addRow(kind)} disabled={saveMut.isPending}>{ADD_LABEL[kind]}</button>}
                  </div>
                );
              })}

              {data.savedAt && !editing && (
                <div className="ac-muted" style={{ fontSize: 12, marginBottom: 'var(--ew-s-3)' }}>
                  Last saved {fmtDateTime(data.savedAt)}{data.savedBy ? ` by ${data.savedBy}` : ''}.
                </div>
              )}

              {data.periods.length > 0 && (
                <div className="ac-hist">
                  <h4>Month-end history</h4>
                  <div className="ew-table-scroll">
                    <table className="ac-tbl">
                      <thead>
                        <tr><th className="t">Month</th><th>Lines</th><th>Material</th><th>Equipment</th><th>Subcontracts</th><th>Total accrued</th></tr>
                      </thead>
                      <tbody>
                        {data.periods.map((p) => (
                          <tr key={p.period} className={p.period === period ? 'sel' : undefined}>
                            <td className="t">
                              <button className="ac-link" disabled={editing} onClick={() => setPeriod(p.period)}>{periodLabel(p.period)}</button>
                            </td>
                            <td>{p.lines}</td><td>{money(p.material)}</td><td>{money(p.equipment)}</td><td>{money(p.subcontract)}</td><td><b>{money(p.total)}</b></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          );
        }}
      </StateView>
    </>
  );
}
